import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import SanityImage from "gatsby-plugin-sanity-image"
import useBreakpoints from "hooks/useBreakpoints"
import Link from "components/Link"
import Container from "components/Container"
import theme from "styles/theme"

const LeadershipGrid = ({ leaders }) => (
  <Container
    css={{
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(auto, 354px))",
      justifyContent: "center",
      gap: 38,
      maxWidth: 1140,
      padding: "104px 0",
      margin: "0 auto",
      [theme.tablet]: {
        padding: 11,
        gap: 10,
      },
      [theme.mobile]: {
        gridTemplateColumns: "repeat(2, minmax(auto, 354px))",
      },
      [theme.mobilePortrait]: {
        gridTemplateColumns: "1fr",
      },
    }}
  >
    {leaders.map((leader, index) => (
      <LeaderTile key={index} {...leader} />
    ))}
  </Container>
)

export default LeadershipGrid

const LeaderTile = ({ name, slug, position, affiliation, headshot }) => {
  const media = useBreakpoints()

  return (
    <Link
      href={slug.current}
      css={{
        background: "#fff",
        borderBottom: `8px solid ${theme.midBlue}`,
        transition: "border-bottom-color 250ms, box-shadow 250ms",
        ":hover": {
          boxShadow: theme.cardShadow,
          borderBottomColor: theme.deepBlue,
        },
      }}
    >
      <SanityImage
        {...headshot}
        width={354}
        height={media.mobile ? 255 : 316}
        alt={name}
        config={{ fit: "min" }}
        css={{ display: "block" }}
      />

      <div
        css={{
          ...theme.previewText,
          padding: "20px 30px",
          color: theme.gray,
          [theme.tablet]: {
            padding: "14px 20px",
          },
        }}
      >
        <h3
          css={{ ...theme.cardTitles, color: theme.textGray, marginBottom: 1 }}
        >
          {name}
        </h3>
        <div>{position}</div>
        {affiliation && <div css={{ fontStyle: "italic" }}>{affiliation}</div>}
      </div>
    </Link>
  )
}

LeaderTile.propTypes = {
  name: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  affiliation: PropTypes.string,
  headshot: PropTypes.shape({
    hotspot: PropTypes.object,
    crop: PropTypes.object,
    asset: PropTypes.object.isRequired,
  }).isRequired,
  slug: PropTypes.shape({ current: PropTypes.string.isRequired }).isRequired,
}

LeadershipGrid.propTypes = {
  leaders: PropTypes.arrayOf(PropTypes.shape(LeaderTile.propTypes).isRequired)
    .isRequired,
}

export const query = graphql`
  fragment LeadershipGridBlock on SanityLeadershipGrid {
    leaders {
      name
      slug {
        current
      }
      position
      affiliation
      headshot {
        ...Image
      }
    }
  }
`
